<!-- 
화면명 : Breadcrumb.vue
설명   : 상단 경로(Breadcrumb) 표시 컴포넌트
         - PC: 전체 풀경로 (예: SAMWOO ERP > 수불관리 > 재고조회(영업사원용) [SM303])
         - 모바일: 현재 메뉴명만 간결하게 표시 (예: 재고조회(영업사원용))
-->
<template>
  <div class="flex items-center text-[#1e3a5f] font-bold min-w-0 mr-2" :class="customClass">
    <!-- 파란색 동그라미 아이콘 -->
    <span class="inline-flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 mr-1.5 text-blue-600 flex-shrink-0">
      <svg viewBox="0 0 24 24" class="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-current">
        <circle cx="12" cy="12" r="10" stroke="#1d4ed8" stroke-width="2" fill="#3b82f6" />
        <circle cx="12" cy="12" r="4" fill="#ffffff" />
      </svg>
    </span>

    <span class="tracking-tight truncate text-sm sm:text-base xl:text-lg font-semibold flex items-center">
      <!-- PC 버전: 풀경로 prefix (예: SAMWOO ERP > 수불관리 > ) -->
      <span class="hidden xl:inline text-xs xl:text-sm text-gray-500 font-normal mr-1">
        <span v-for="(part, idx) in breadcrumbPrefix" :key="idx">
          {{ part }} &gt;&nbsp;
        </span>
      </span>

      <!-- 현재 메뉴명 (모바일/PC 공통: 화면 메인 타이틀로 크게 강조) -->
      <span class="text-blue-950 font-bold text-sm sm:text-base xl:text-md">{{ currentMenuTitle }}</span>

      <!-- 프로그램 ID (PC에서만 표시, 예: [SM303]) -->
      <span v-if="currentMenuProgramId" class="hidden xl:inline text-xs xl:text-sm text-gray-400 ml-1.5 font-normal">
        [{{ currentMenuProgramId }}]
      </span>
    </span>
  </div>
</template>

<script setup>
import { computed, defineProps } from "vue";
import { useRoute } from "vue-router";
import { useUserStore } from "@/store/userStore";
import { getMenuByPath, MOCK_MENU_LIST } from "@/api/menu";

const props = defineProps({
  // 수동 경로 지정이 필요한 경우 (생략 시 현재 route.path 자동 조회)
  path: {
    type: String,
    default: "",
  },
  // 타이틀 강제 지정이 필요한 경우
  title: {
    type: String,
    default: "",
  },
  // 추가 스타일 클래스
  customClass: {
    type: [String, Object, Array],
    default: "",
  },
});

const route = useRoute();
const userStore = useUserStore();

// 현재 경로에 매칭되는 메뉴 정보 (userStore.myMenu 우선, 없을 시 MOCK_MENU_LIST)
const currentMenu = computed(() => {
  const currentPath = props.path || route?.path || "";
  const list = userStore.myMenu && userStore.myMenu.length > 0 ? userStore.myMenu : MOCK_MENU_LIST;
  return getMenuByPath(currentPath, list) || null;
});

// breadcrumb prefix (풀경로 중 현재 메뉴명 이전 단계들, 예: ["SAMWOO ERP", "수불관리"])
const breadcrumbPrefix = computed(() => {
  const menu = currentMenu.value;
  if (Array.isArray(menu?.breadcrumb) && menu.breadcrumb.length > 1) {
    return menu.breadcrumb.slice(0, -1);
  }
  if (typeof menu?.breadcrumb === "string") {
    const parts = menu.breadcrumb.split(">").map((s) => s.trim());
    if (parts.length > 1) return parts.slice(0, -1);
  }
  if (menu?.category) {
    return ["SAMWOO ERP", menu.category];
  }
  return ["SAMWOO ERP"];
});

// 현재 메뉴명 (예: "재고조회(영업사원용)")
const currentMenuTitle = computed(() => {
  if (props.title) return props.title;
  const menu = currentMenu.value;
  if (menu?.title) return menu.title;
  if (Array.isArray(menu?.breadcrumb) && menu.breadcrumb.length > 0) {
    return menu.breadcrumb[menu.breadcrumb.length - 1];
  }
  return menu?.name || route?.name || "";
});

// 프로그램 ID (예: "SM303")
const currentMenuProgramId = computed(() => {
  const menu = currentMenu.value;
  return menu?.programId || menu?.code || "";
});
</script>
